/**
 * Notification & Alert Helper Library
 *
 * This library provides clean APIs for sending notifications and alerts.
 * All notification/alert creation goes through the backend Edge Function
 * to ensure proper security, preference checking, and alert resolution.
 *
 * @see docs/notifications-system-v2.md for complete system architecture
 * @see supabase/functions/process-notification for backend implementation
 */

import { supabase } from './supabase'

/**
 * Send a personal notification to specific users
 *
 * Notifications are user-controlled messages like mentions, comments, and replies.
 * They respect each user's notification preferences.
 *
 * @param {Object} params - Notification parameters
 * @param {string} params.notification_type - Type of notification ('mentions', 'comments', 'replies', 'assignments', etc.)
 * @param {string[]} params.user_ids - Array of user profile IDs to notify
 * @param {string} params.title - Notification title
 * @param {string} params.message - Notification message
 * @param {string} [params.space_id] - Optional space/project ID for context
 * @param {string} [params.link_url] - Optional URL to navigate to when clicked
 * @param {string} [params.entity_type] - Optional entity type ('booking', 'checklist', 'message', etc.)
 * @param {string} [params.entity_id] - Optional entity ID
 * @param {string} [params.actor_id] - User ID who triggered this notification
 * @param {string} [params.group_key] - Optional grouping key for similar notifications
 * @param {Object} [params.metadata] - Optional additional metadata
 * @returns {Promise<{success: boolean, notifications_created: number, notifications_skipped: number}>}
 *
 * @example
 * // Send a mention notification
 * await sendNotification({
 *   notification_type: 'mentions',
 *   user_ids: ['user-uuid-1', 'user-uuid-2'],
 *   title: 'You were mentioned',
 *   message: 'John Smith mentioned you in Project Alpha chat',
 *   link_url: '/chat?conversation=abc-123',
 *   entity_type: 'chat_message',
 *   entity_id: 'message-uuid',
 *   actor_id: 'john-uuid',
 *   group_key: 'chat_abc-123_mentions'
 * })
 *
 * @example
 * // Send a comment notification
 * await sendNotification({
 *   notification_type: 'comments',
 *   user_ids: ['owner-uuid'],
 *   title: 'New comment on your checklist',
 *   message: 'Sarah Jones commented on "Safety Inspection Checklist"',
 *   space_id: 'space-uuid',
 *   link_url: '/space/space-uuid/checklist/checklist-uuid',
 *   entity_type: 'checklist',
 *   entity_id: 'checklist-uuid',
 *   actor_id: 'sarah-uuid'
 * })
 */
export async function sendNotification({
  notification_type,
  user_ids,
  title,
  message,
  space_id,
  link_url,
  entity_type,
  entity_id,
  actor_id,
  group_key,
  metadata
}) {
  try {
    // Validate required fields
    if (!notification_type) {
      throw new Error('notification_type is required')
    }
    if (!user_ids || !Array.isArray(user_ids) || user_ids.length === 0) {
      throw new Error('user_ids array is required and must not be empty')
    }
    if (!title || !message) {
      throw new Error('title and message are required')
    }

    // Call Edge Function
    const { data, error } = await supabase.functions.invoke('process-notification', {
      body: {
        type: 'notification',
        notification_type,
        user_ids,
        title,
        message,
        space_id,
        link_url,
        entity_type,
        entity_id,
        actor_id,
        group_key,
        metadata
      }
    })

    if (error) {
      console.error('Failed to send notification:', error)
      throw error
    }

    if (!data.success) {
      throw new Error(data.error || 'Failed to send notification')
    }

    console.log(`Notification sent: ${data.notifications_created} created, ${data.notifications_skipped} skipped`)

    return {
      success: true,
      notifications_created: data.notifications_created,
      notifications_skipped: data.notifications_skipped
    }

  } catch (error) {
    console.error('Error in sendNotification:', error)
    // Don't throw - notifications should never block the UI
    return {
      success: false,
      notifications_created: 0,
      notifications_skipped: 0,
      error: error.message
    }
  }
}

/**
 * Trigger a role-based alert for Admins and Managers
 *
 * Alerts are admin-controlled notifications about system/tool events.
 * They are automatically sent to all Admins/Managers based on:
 * - Company alert settings (or space overrides)
 * - User role (Admin/Manager only)
 * - User muting preferences
 * - Space team membership (for Managers)
 *
 * @param {Object} params - Alert parameters
 * @param {string} params.tool_id - Tool identifier ('bookings', 'checklists', 'checkins', 'qualifications', 'team', 'compliance', 'matrix', 'calendar', 'message_board', 'docs')
 * @param {string} params.alert_id - Alert identifier (e.g., 'new_booking_created', 'cert_expired', 'missed_checkin')
 * @param {string} params.title - Alert title
 * @param {string} params.message - Alert message
 * @param {string} [params.space_id] - Optional space/project ID (required for space-specific alerts)
 * @param {string} [params.link_url] - Optional URL to navigate to when clicked
 * @param {string} [params.entity_type] - Optional entity type
 * @param {string} [params.entity_id] - Optional entity ID
 * @param {string} [params.actor_id] - User ID who triggered this alert (optional for system alerts)
 * @param {string} [params.group_key] - Optional grouping key
 * @param {Object} [params.metadata] - Optional additional metadata
 * @returns {Promise<{success: boolean, notifications_created: number, notifications_skipped: number}>}
 *
 * @example
 * // Trigger a booking alert
 * await triggerAlert({
 *   tool_id: 'bookings',
 *   alert_id: 'new_booking_created',
 *   space_id: 'space-uuid',
 *   title: 'New Booking Created',
 *   message: 'John Smith created a new booking: First Aid Training',
 *   link_url: '/space/space-uuid/bookings/booking-uuid',
 *   entity_type: 'booking',
 *   entity_id: 'booking-uuid',
 *   actor_id: 'john-uuid',
 *   metadata: {
 *     booking_name: 'First Aid Training',
 *     booking_date: '2025-02-15'
 *   }
 * })
 *
 * @example
 * // Trigger a certificate expiry alert
 * await triggerAlert({
 *   tool_id: 'qualifications',
 *   alert_id: 'cert_expired',
 *   space_id: 'space-uuid',
 *   title: 'Certificate Expired',
 *   message: "Sarah Jones's First Aid certificate has expired",
 *   link_url: '/space/space-uuid/team/sarah-uuid/requirements',
 *   entity_type: 'requirement',
 *   entity_id: 'requirement-uuid',
 *   actor_id: null, // System-generated
 *   metadata: {
 *     cert_name: 'First Aid',
 *     expiry_date: '2025-01-05',
 *     user_name: 'Sarah Jones'
 *   }
 * })
 *
 * @example
 * // Trigger a missed check-in alert
 * await triggerAlert({
 *   tool_id: 'checkins',
 *   alert_id: 'missed_checkin',
 *   space_id: 'space-uuid',
 *   title: 'Missed Check-in',
 *   message: 'Mike Brown missed check-in at Construction Site A (expected at 08:00)',
 *   link_url: '/space/space-uuid/checkins',
 *   entity_type: 'checkin',
 *   entity_id: 'site-uuid',
 *   actor_id: null
 * })
 */
export async function triggerAlert({
  tool_id,
  alert_id,
  title,
  message,
  space_id,
  link_url,
  entity_type,
  entity_id,
  actor_id,
  group_key,
  metadata
}) {
  try {
    // Validate required fields
    if (!tool_id) {
      throw new Error('tool_id is required')
    }
    if (!alert_id) {
      throw new Error('alert_id is required')
    }
    if (!title || !message) {
      throw new Error('title and message are required')
    }

    // Call Edge Function
    const { data, error } = await supabase.functions.invoke('process-notification', {
      body: {
        type: 'alert',
        tool_id,
        alert_id,
        title,
        message,
        space_id,
        link_url,
        entity_type,
        entity_id,
        actor_id,
        group_key,
        metadata
      }
    })

    if (error) {
      console.error('Failed to trigger alert:', error)
      throw error
    }

    if (!data.success) {
      throw new Error(data.error || 'Failed to trigger alert')
    }

    console.log(`Alert triggered: ${data.notifications_created} sent, ${data.notifications_skipped} skipped`)

    return {
      success: true,
      notifications_created: data.notifications_created,
      notifications_skipped: data.notifications_skipped
    }

  } catch (error) {
    console.error('Error in triggerAlert:', error)
    // Don't throw - alerts should never block the UI
    return {
      success: false,
      notifications_created: 0,
      notifications_skipped: 0,
      error: error.message
    }
  }
}

/**
 * Helper function to send notification without blocking
 * Fire-and-forget pattern for notifications that shouldn't delay user actions
 *
 * @param {Object} params - Same as sendNotification
 * @returns {void}
 *
 * @example
 * // Send notification without waiting
 * sendNotificationAsync({
 *   notification_type: 'mentions',
 *   user_ids: [userId],
 *   title: 'You were mentioned',
 *   message: 'Someone mentioned you',
 *   actor_id: currentUserId
 * })
 * // Code continues immediately, notification sends in background
 */
export function sendNotificationAsync(params) {
  sendNotification(params).catch(error => {
    console.error('Background notification failed:', error)
  })
}

/**
 * Helper function to trigger alert without blocking
 * Fire-and-forget pattern for alerts that shouldn't delay user actions
 *
 * @param {Object} params - Same as triggerAlert
 * @returns {void}
 *
 * @example
 * // Trigger alert without waiting
 * triggerAlertAsync({
 *   tool_id: 'bookings',
 *   alert_id: 'booking_cancelled',
 *   space_id: spaceId,
 *   title: 'Booking Cancelled',
 *   message: `Booking "${bookingName}" was cancelled`,
 *   link_url: `/space/${spaceId}/bookings`,
 *   actor_id: currentUserId
 * })
 * // Code continues immediately, alert sends in background
 */
export function triggerAlertAsync(params) {
  triggerAlert(params).catch(error => {
    console.error('Background alert failed:', error)
  })
}

/**
 * Batch send notifications to multiple users efficiently
 * Useful when you need to notify many users about the same event
 *
 * @param {Object} params - Notification parameters
 * @param {string[]} params.user_ids - Array of user IDs (can be large)
 * @param {number} [params.batch_size=50] - Number of users per batch
 * @param {Object} params.notification - Rest of notification parameters
 * @returns {Promise<{success: boolean, total_created: number, total_skipped: number}>}
 *
 * @example
 * // Notify all team members about a new document
 * await batchSendNotifications({
 *   user_ids: teamMemberIds, // Could be 100+ users
 *   batch_size: 50,
 *   notification: {
 *     notification_type: 'documents',
 *     title: 'New Document Shared',
 *     message: 'Safety Procedures Manual has been uploaded',
 *     space_id: spaceId,
 *     link_url: `/space/${spaceId}/docs`,
 *     actor_id: currentUserId
 *   }
 * })
 */
export async function batchSendNotifications({
  user_ids,
  batch_size = 50,
  notification
}) {
  try {
    let totalCreated = 0
    let totalSkipped = 0

    // Split into batches
    for (let i = 0; i < user_ids.length; i += batch_size) {
      const batch = user_ids.slice(i, i + batch_size)

      const result = await sendNotification({
        ...notification,
        user_ids: batch
      })

      totalCreated += result.notifications_created
      totalSkipped += result.notifications_skipped
    }

    return {
      success: true,
      total_created: totalCreated,
      total_skipped: totalSkipped
    }

  } catch (error) {
    console.error('Error in batchSendNotifications:', error)
    return {
      success: false,
      total_created: 0,
      total_skipped: 0,
      error: error.message
    }
  }
}

/**
 * Validate alert exists in system
 * Useful for checking if alert ID is valid before attempting to trigger
 *
 * @param {string} toolId - Tool identifier
 * @param {string} alertId - Alert identifier
 * @returns {boolean} True if alert exists
 *
 * @example
 * if (isValidAlert('bookings', 'new_booking_created')) {
 *   await triggerAlert({ ... })
 * }
 */
export function isValidAlert(toolId, alertId) {
  // Import alert definitions
  try {
    const { isValidAlert: checkValid } = require('@/config/alertDefinitions')
    return checkValid(toolId, alertId)
  } catch (error) {
    console.error('Could not validate alert:', error)
    return false
  }
}

/**
 * Get notification/alert statistics
 * Useful for debugging and monitoring
 *
 * @returns {Promise<{unread_count: number, recent_notifications: Array}>}
 *
 * @example
 * const stats = await getNotificationStats()
 * console.log(`You have ${stats.unread_count} unread notifications`)
 */
export async function getNotificationStats() {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return { unread_count: 0, recent_notifications: [] }

    // Get user profile
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('id')
      .eq('auth_id', user.id)
      .single()

    if (!profile) return { unread_count: 0, recent_notifications: [] }

    // Get unread count
    const { count: unread_count } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', profile.id)
      .eq('read', false)

    // Get recent notifications
    const { data: recent_notifications } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', profile.id)
      .order('created_date', { ascending: false })
      .limit(10)

    return {
      unread_count: unread_count || 0,
      recent_notifications: recent_notifications || []
    }

  } catch (error) {
    console.error('Error getting notification stats:', error)
    return {
      unread_count: 0,
      recent_notifications: []
    }
  }
}

// Export all functions
export default {
  sendNotification,
  triggerAlert,
  sendNotificationAsync,
  triggerAlertAsync,
  batchSendNotifications,
  isValidAlert,
  getNotificationStats
}
